import * as client from 'openid-client';
import { z } from 'zod';
import { loadSecrets, storeSecrets } from './secret-storage';
import config from '../../config';
import output from '../../utils/output-writer';

const TokenDataSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.number(),
});

type TokenData = z.infer<typeof TokenDataSchema>;

let oauthConfig: client.Configuration | undefined;

async function getConfig(): Promise<client.Configuration> {
  if (oauthConfig) return oauthConfig;
  const server = new URL(`https://${config.auth.domain}`);
  oauthConfig = await client.discovery(
    server,
    config.auth.clientId,
    undefined,
    client.None()
  );
  return oauthConfig;
}

function normalizeTokenResponse(
  response: client.TokenEndpointResponse,
  fallbackRefreshToken?: string
): TokenData {
  if (!response.expires_in) {
    throw new Error('Token response missing expires_in field');
  }
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? fallbackRefreshToken,
    expiresAt: Date.now() + response.expires_in * 1000,
  };
}

export async function performDeviceFlow(): Promise<string> {
  const cfg = await getConfig();
  const deviceAuthResponse = await client.initiateDeviceAuthorization(cfg, {
    scope: 'offline_access',
    audience: config.auth.audience,
  });
  output.log(
    `\nPlease go to: ${deviceAuthResponse.verification_uri_complete ?? deviceAuthResponse.verification_uri}\nEnter code: ${deviceAuthResponse.user_code}\n`
  );
  const tokenResponse = await client.pollDeviceAuthorizationGrant(
    cfg,
    deviceAuthResponse
  );
  const tokenData = normalizeTokenResponse(tokenResponse);
  await storeSecrets({
    service: config.keytar.service,
    account: config.keytar.tokens,
    data: tokenData,
  });
  return tokenData.accessToken;
}

async function refreshAccessToken(
  refreshToken: string
): Promise<string | null> {
  const cfg = await getConfig();
  try {
    const tokenResponse = await client.refreshTokenGrant(cfg, refreshToken, {
      audience: config.auth.audience,
    });
    const tokenData = normalizeTokenResponse(tokenResponse, refreshToken);
    await storeSecrets({
      service: config.keytar.service,
      account: config.keytar.tokens,
      data: tokenData,
    });
    return tokenData.accessToken;
  } catch {
    return null;
  }
}

export async function getAccessToken(): Promise<string> {
  const minimalRequiredTtlMs = 60_000;
  const stored = await loadSecrets({
    service: config.keytar.service,
    account: config.keytar.tokens,
    schema: TokenDataSchema,
  });
  if (
    stored?.accessToken &&
    stored.expiresAt > Date.now() + minimalRequiredTtlMs
  ) {
    return stored.accessToken;
  }
  if (stored?.refreshToken) {
    const accessToken = await refreshAccessToken(stored.refreshToken);
    if (accessToken) return accessToken;
  }
  throw new Error(
    "Unable to fetch the access token. Please make sure you're logged in before using a tool"
  );
}

export default { performDeviceFlow, getAccessToken };
